import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Button,
  Modal
} from '@mui/material';
import config from '../config';

const server_URL = config.SERVER_URL;

const Reports = () => {
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showUnsubscribedModal, setShowUnsubscribedModal] = useState(false);
  const [unsubscribedList, setUnsubscribedList] = useState([]);
  const [isLoadingUnsubscribed, setIsLoadingUnsubscribed] = useState(false);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${server_URL}/api/reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          method: 'list',
          user_id: JSON.parse(localStorage.getItem('user')).id
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch reports');
      }

      const data = await response.json();
      if (data.status === '1') {
        setReports(data.data || []);
      } else {
        console.error('Error:', data.error);
        setReports([]);
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      setReports([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUnsubscribedList = async () => {
    setIsLoadingUnsubscribed(true);
    try {
      const response = await fetch(`${server_URL}/api/reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          method: 'unsubscribed'
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch unsubscribed list');
      }

      const data = await response.json();
      if (data.status === '1') {
        setUnsubscribedList(data.data || []);
        setShowUnsubscribedModal(true);
      }
    } catch (error) {
      console.error('Error fetching unsubscribed list:', error);
    } finally {
      setIsLoadingUnsubscribed(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return { bg: '#e8f5e9', color: '#2e7d32' };
      case 'processing':
        return { bg: '#e3f2fd', color: '#1976d2' };
      case 'scheduled':
        return { bg: '#fff3e0', color: '#ed6c02' };
      case 'error':
        return { bg: '#ffebee', color: '#d32f2f' };
      default:
        return { bg: '#f5f5f5', color: '#757575' };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not scheduled';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    
    return `${day}-${month}-${year}, ${displayHours}:${minutes}:${seconds} ${ampm}`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4">Reports</Typography>
        <Button 
          variant="contained" 
          color="primary"
          onClick={fetchUnsubscribedList}
        >
          View Unsubscribed List
        </Button>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Campaign Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Subject</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Total Emails</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Sent</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Failed</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Opened</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Open Rate</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Scheduled Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="textSecondary">
                      No campaign reports available
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                reports.map((report) => {
                  const openRate = report.sent_count > 0 
                    ? ((report.opened_count / report.sent_count) * 100).toFixed(2) 
                    : '0.00';
                  const statusColors = getStatusColor(report.status);
                  
                  return (
                    <TableRow key={report.id} hover>
                      <TableCell>{report.name}</TableCell>
                      <TableCell>{report.subject}</TableCell>
                      <TableCell align="center">{report.total_count}</TableCell>
                      <TableCell align="center">{report.sent_count}</TableCell>
                      <TableCell align="center">{report.failed_count}</TableCell>
                      <TableCell align="center">{report.opened_count}</TableCell>
                      <TableCell align="center">{openRate}%</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={report.status}
                          sx={{
                            backgroundColor: statusColors.bg,
                            color: statusColors.color,
                            fontWeight: 500
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {formatDate(report.schedule_time)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Modal
        open={showUnsubscribedModal}
        onClose={() => setShowUnsubscribedModal(false)}
        aria-labelledby="unsubscribed-modal-title"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 600,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          maxHeight: '80vh',
          overflow: 'auto'
        }}>
          <Typography id="unsubscribed-modal-title" variant="h6" component="h2" gutterBottom>
            Unsubscribed Email List
          </Typography>
          {isLoadingUnsubscribed ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Unsubscribed Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unsubscribedList.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.email}</TableCell>
                      <TableCell>{item.date}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default Reports;
